import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase/config';
import { updateUserPhoneComprehensive } from '@/lib/firebase/queries/phone-management';
import type { User } from '@/types';

interface UserDetailsProps {
  user: User;
  onUpdate?: () => void;
}

const formSchema = z.object({
  firstNameEn: z.string().min(2, 'First name must be at least 2 characters'),
  lastNameEn: z.string().min(2, 'Last name must be at least 2 characters'),
  firstNameAr: z.string().min(2, 'First name must be at least 2 characters'),
  lastNameAr: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string()
    .regex(/^05\d{8}$/, 'Phone number must be in format 05XXXXXXXX (10 digits starting with 05)')
    .length(10, 'Phone number must be exactly 10 digits')
    .optional(),
});

export function UserDetails({ user, onUpdate }: UserDetailsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { t, dir } = useLanguage();
  const isRTL = dir === 'rtl';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstNameEn: user.firstNameEn || '',
      lastNameEn: user.lastNameEn || '',
      firstNameAr: user.firstNameAr || '',
      lastNameAr: user.lastNameAr || '',
      email: user.email || '',
      phone: user.phone || '',
    },
  });

  // Update form when user data changes
  useEffect(() => {
    form.reset({
      firstNameEn: user.firstNameEn || '',
      lastNameEn: user.lastNameEn || '',
      firstNameAr: user.firstNameAr || '',
      lastNameAr: user.lastNameAr || '',
      email: user.email || '',
      phone: user.phone || '',
    });
  }, [user, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);
      
      // Check if phone number has changed
      const oldPhone = user.phone || '';
      const newPhone = values.phone || '';
      
      if (oldPhone !== newPhone && newPhone) {
        // Use comprehensive phone update function
        const phoneUpdateResult = await updateUserPhoneComprehensive(user.id, oldPhone, newPhone);
        
        if (!phoneUpdateResult.success) {
          toast.error(phoneUpdateResult.error || t('dashboard.admin.users.errors.phoneUpdateError'));
          return;
        }
        
        // Update other user fields (excluding phone since it's already updated)
        const { phone, ...otherFields } = values;
        await updateDoc(doc(db, 'users', user.id), {
          ...otherFields,
          updatedAt: new Date(),
        });
        
        toast.success(t('dashboard.admin.users.errors.comprehensiveUpdateSuccess'));
      } else {
        // Phone hasn't changed or is empty, just update user document normally
        await updateDoc(doc(db, 'users', user.id), {
          ...values,
          updatedAt: new Date(),
        });
        
        toast.success(t('dashboard.admin.users.details.updateSuccess'));
      }
      
      onUpdate?.();
    } catch (error) {
      console.error('Error updating user details:', error);
      toast.error(t('dashboard.admin.users.details.updateError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      
      const storageRef = ref(storage, `users/${user.id}/profile-photo`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      await updateDoc(doc(db, 'users', user.id), {
        photoURL: downloadURL,
        updatedAt: new Date(),
      });

      toast.success(t('dashboard.admin.users.details.photoUpdateSuccess'));
      onUpdate?.();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(t('dashboard.admin.users.details.photoUpdateError'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      await updateDoc(doc(db, 'users', user.id), {
        photoURL: null,
        updatedAt: new Date(),
      });

      toast.success(t('dashboard.admin.users.details.photoRemoveSuccess'));
      onUpdate?.();
    } catch (error) {
      console.error('Error removing photo:', error);
      toast.error(t('dashboard.admin.users.details.photoRemoveError'));
    }
  };

  return (
    <div className="space-y-4" dir={dir}>
      {/* Profile Photo Section */}
      <Card className="p-4">
        <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} justify-between items-center mb-4`}>
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h3 className="text-lg font-semibold">{t('dashboard.admin.users.details.profilePhoto')}</h3>
            <p className="text-sm text-muted-foreground">{t('dashboard.admin.users.details.profilePhotoDesc')}</p>
          </div>
        </div>
        <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} items-center gap-4`}>
          <Avatar className="h-16 w-16">
            <AvatarImage src={user.photoURL} alt={`${user.firstNameEn} ${user.lastNameEn}`} />
            <AvatarFallback className="text-base">
              {user.firstNameEn?.[0]}{user.lastNameEn?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-2">
            <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} gap-2`}>
              <Button
                variant="outline"
                size="sm"
                disabled={isUploading}
                onClick={() => document.getElementById('photo-upload')?.click()}
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {t('dashboard.admin.users.details.uploadPhoto')}
              </Button>
              {user.photoURL && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRemovePhoto}
                >
                  <X className="h-4 w-4" />
                  {t('dashboard.admin.users.details.removePhoto')}
                </Button>
              )}
            </div>
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoUpload}
            />
          </div>
        </div>
      </Card>

      {/* User Details Form */}
      <Card className="p-4">
        <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} justify-between items-center mb-4`}>
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h3 className="text-lg font-semibold">{t('dashboard.admin.users.details.personalInfo')}</h3>
            <p className="text-sm text-muted-foreground">{t('dashboard.admin.users.details.personalInfoDesc')}</p>
          </div>
        </div>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstNameEn" className={isRTL ? 'text-right' : 'text-left'}>{t('dashboard.admin.users.details.firstNameEn')}</Label>
              <Input
                id="firstNameEn"
                {...form.register('firstNameEn')}
                className={isRTL ? 'text-right' : 'text-left'}
              />
              {form.formState.errors.firstNameEn && (
                <p className="text-sm text-destructive">{form.formState.errors.firstNameEn.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="lastNameEn" className={isRTL ? 'text-right' : 'text-left'}>{t('dashboard.admin.users.details.lastNameEn')}</Label>
              <Input
                id="lastNameEn"
                {...form.register('lastNameEn')}
                className={isRTL ? 'text-right' : 'text-left'}
              />
              {form.formState.errors.lastNameEn && (
                <p className="text-sm text-destructive">{form.formState.errors.lastNameEn.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="firstNameAr" className={isRTL ? 'text-right' : 'text-left'}>{t('dashboard.admin.users.details.firstNameAr')}</Label>
              <Input
                id="firstNameAr"
                {...form.register('firstNameAr')}
                className={isRTL ? 'text-right' : 'text-left'}
              />
              {form.formState.errors.firstNameAr && (
                <p className="text-sm text-destructive">{form.formState.errors.firstNameAr.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="lastNameAr" className={isRTL ? 'text-right' : 'text-left'}>{t('dashboard.admin.users.details.lastNameAr')}</Label>
              <Input
                id="lastNameAr"
                {...form.register('lastNameAr')}
                className={isRTL ? 'text-right' : 'text-left'}
              />
              {form.formState.errors.lastNameAr && (
                <p className="text-sm text-destructive">{form.formState.errors.lastNameAr.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className={isRTL ? 'text-right' : 'text-left'}>{t('dashboard.admin.users.details.email')}</Label>
              <Input
                id="email"
                type="email"
                {...form.register('email')}
                className={isRTL ? 'text-right' : 'text-left'}
              />
              {form.formState.errors.email && (
                <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className={isRTL ? 'text-right' : 'text-left'}>{t('dashboard.admin.users.details.phone')}</Label>
              <Input
                id="phone"
                {...form.register('phone')}
                className={isRTL ? 'text-right' : 'text-left'}
              />
              {form.formState.errors.phone && (
                <p className="text-sm text-destructive">{form.formState.errors.phone.message}</p>
              )}
            </div>
          </div>
          
          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} gap-2 pt-4`}>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('dashboard.admin.users.details.saving')}
                </>
              ) : (
                t('dashboard.admin.users.details.saveChanges')
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
} 